import { gatewayApiAgent } from 'common/api/apiAgents';
import { SortingDirection } from 'common/api-types/common';
import {
  BinLocationsListingResponse as BinLocationsListingResponseGateway,
  ExtendedBinLocation,
} from 'common/api-types/gateway';
import {
  ListMetaWoltWarehouseItems,
  ListMetaWarehouseItems,
  SortBy,
  ListMetaWoltItemChangeLog,
  WoltWarehouseItem,
  WarehouseItem,
  BinLocationType,
  BinLocationCompact,
  BinLocationsListingResponse,
  BatchesForDryRunResult,
  CountLowInventoryItemsDryRunResult,
  ShrinkExpiredItemsDryRunResult,
  ShrinkItemsExpiringTodayDryRunResult,
  CountUnreliableItemsDryRunResult,
  MoveToBeDiscountedDryRunResult,
  DryRunEligibilitySummaryResult,
  WarehouseTaskType,
} from 'common/api-types/warehouse';
import { FetchListItemsParams } from 'common/types';

export const fetchBinLocation = (
  warehouseId: string,
  binLocationId: string,
): Promise<ExtendedBinLocation> =>
  gatewayApiAgent
    .get(`/public/v1/warehouses/${warehouseId}/bin-locations/${binLocationId}`)
    .then((res) => res.data);

export type FetchBinLocationsParams = FetchListItemsParams & {
  locationType?: BinLocationType;
  pickable?: boolean;
  minTemperature?: number;
  maxTemperature?: number;
};

/**
 * Fetch a list of bin locations for a warehouse. This is a fairly heavy endpoint which
 * contains a lot of details about the bin locations. Compared to `fetchExtendedBinLocations`,
 * the `assignedProducts` property is simplified.
 * @param warehouseId - The ID of the warehouse to fetch bin locations for
 * @param params - The parameters to fetch bin locations
 * @returns A promise that resolves to the bin locations
 */
export const fetchBinLocations = (
  warehouseId: string,
  params: FetchListItemsParams,
): Promise<BinLocationsListingResponse> =>
  gatewayApiAgent
    .get(`/warehouse/public/v1/warehouses/${warehouseId}/bin-locations`, { params })
    .then((res) => res.data);

/**
 * Fetch a list of bin locations for a warehouse. This is a fairly heavy endpoint which
 * contains a lot of details about the bin locations. It is "extended" in the sense
 * that it's the unadulterated response from wm-warehouse-api.
 * @param warehouseId - The ID of the warehouse to fetch bin locations for
 * @param params - The parameters to fetch bin locations
 * @returns A promise that resolves to the bin locations
 */
export const fetchExtendedBinLocations = (
  warehouseId: string,
  params: FetchBinLocationsParams,
): Promise<BinLocationsListingResponseGateway> =>
  gatewayApiAgent
    .get(`/public/v1/warehouses/${warehouseId}/bin-locations`, { params })
    .then((res) => res.data);

export const fetchCompactBinLocations = (warehouseId: string): Promise<BinLocationCompact[]> =>
  gatewayApiAgent
    .get(`/warehouse/public/v1/warehouses/${warehouseId}/bin-location-compact`)
    .then((res) => res.data?.items ?? []);

export type FetchItemsParams = {
  q?: string;
  pageSize?: number;
  sortBy?: SortBy;
  sortingType?: SortingDirection;
  countryCode?: string;
  binLocationIds?: string[];
  fromExpiryDate?: string;
  toExpiryDate?: string;
};

export const searchWoltItems = (
  warehouseId: string,
  params: FetchItemsParams,
): Promise<ListMetaWoltWarehouseItems> => {
  return gatewayApiAgent
    .get(`/warehouse/public/v1/wolt/warehouses/${warehouseId}/items`, {
      params: {
        page: 0,
        pageSize: 50,
        ...params,
      },
    })
    .then((res) => res.data);
};

export const fetchItems = (
  warehouseId: string,
  params: FetchItemsParams & { isInTransit?: boolean },
): Promise<ListMetaWarehouseItems> => {
  return gatewayApiAgent
    .get(`/warehouse/public/v1/warehouses/${warehouseId}/items`, {
      params: {
        pageSize: 50,
        ...params,
      },
    })
    .then((res) => res.data);
};

export const fetchWoltItem = (warehouseId: string, itemId: string): Promise<WoltWarehouseItem> => {
  return gatewayApiAgent
    .get(`/warehouse/public/v1/wolt/warehouses/${warehouseId}/items/${itemId}`)
    .then((res) => res.data);
};

export const fetchItem = (
  warehouseId: string,
  itemId: string,
  countryCode?: string,
): Promise<WarehouseItem> => {
  return gatewayApiAgent
    .get(`/warehouse/public/v1/warehouses/${warehouseId}/items/${itemId}`, {
      params: { countryCode },
    })
    .then((res) => res.data);
};

export const fetchChangeLogByWarehouseAndItem = (
  warehouseId: string,
  itemId: string,
): Promise<ListMetaWoltItemChangeLog> =>
  gatewayApiAgent
    .get(`/warehouse/public/v1/wolt/warehouses/${warehouseId}/items/${itemId}/change-log`, {
      params: { pageSize: 10 },
    })
    .then((res) => res.data);

export const fetchBatchesForDryRun = (
  warehouseId: string,
  productId: string,
  taskType: WarehouseTaskType,
): Promise<BatchesForDryRunResult> =>
  gatewayApiAgent
    .get(`/warehouse/public/v1/tasks/dry-run/${warehouseId}/${productId}/${taskType}/batches`)
    .then((res) => res.data);

export const fetchDryRunCountLowInventoryItems = (
  warehouseId: string,
  productId: string,
): Promise<CountLowInventoryItemsDryRunResult> =>
  gatewayApiAgent
    .get(`/warehouse/public/v1/tasks/dry-run/count-low-inventory-items/${warehouseId}/${productId}`)
    .then((res) => res.data);

export const fetchDryRunEligibilitySummary = (
  warehouseId: string,
  productId: string,
): Promise<DryRunEligibilitySummaryResult> =>
  gatewayApiAgent
    .get(`/warehouse/public/v1/tasks/dry-run/eligibility-summary/${warehouseId}/${productId}`)
    .then((res) => res.data);

export const fetchDryRunShrinkExpiredItems = (
  warehouseId: string,
  locationBatchId: string,
): Promise<ShrinkExpiredItemsDryRunResult> =>
  gatewayApiAgent
    .get(
      `/warehouse/public/v1/tasks/dry-run/shrink-expired-items/${warehouseId}/${locationBatchId}`,
    )
    .then((res) => res.data);

export const fetchDryRunShrinkItemsExpiringToday = (
  warehouseId: string,
  locationBatchId: string,
): Promise<ShrinkItemsExpiringTodayDryRunResult> =>
  gatewayApiAgent
    .get(
      `/warehouse/public/v1/tasks/dry-run/shrink-items-expiring-today/${warehouseId}/${locationBatchId}`,
    )
    .then((res) => res.data);

export const fetchDryRunCountUnreliableItems = (
  warehouseId: string,
  locationBatchId: string,
): Promise<CountUnreliableItemsDryRunResult> =>
  gatewayApiAgent
    .get(
      `/warehouse/public/v1/tasks/dry-run/count-unreliable-items/${warehouseId}/${locationBatchId}`,
    )
    .then((res) => res.data);

export const fetchDryRunMoveToBeDiscounted = (
  warehouseId: string,
  locationBatchId: string,
): Promise<MoveToBeDiscountedDryRunResult> =>
  gatewayApiAgent
    .get(
      `/warehouse/public/v1/tasks/dry-run/move-to-be-discounted/${warehouseId}/${locationBatchId}`,
    )
    .then((res) => res.data);
